import { useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';

/**
 * A hook that tracks page views based on location changes.
 * The tracking callback will fire once when the component mounts and whenever the location changes.
 *
 * @param trackingCallback - Function to call when a page view should be tracked
 * @param enabled - Whether tracking is enabled (default: true)
 *
 * @example
 * ```tsx
 * usePageViewTracking(() => {
 *   analytics.track('myPage', 'viewed', {
 *     Source: 'navigation',
 *     'User ID': userId,
 *   });
 * });
 * ```
 */
export function usePageViewTracking(trackingCallback: () => void, enabled = true) {
  const locationKey = useLocation({ select: (location) => `${location.pathname}${location.searchStr}` });
  const trackedLocationRef = useRef<string | null>(null);
  const callbackRef = useRef(trackingCallback);
  callbackRef.current = trackingCallback;

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (!enabled) {
      return;
    }

    // Only track if we haven't tracked this exact location yet
    if (trackedLocationRef.current !== locationKey) {
      callbackRef.current();
      trackedLocationRef.current = locationKey;
    }
  }, [locationKey, enabled]);
}
